if (!customElements.get('o11-basket')) {
  // London calendar days avoid shopper time-zone and daylight-saving offsets.
  function estimatedDeliveryDate(now, transitDays, cutoffHour, excludedDates, validUntil) {
    if (!Number.isInteger(transitDays) || transitDays < 1 || transitDays > 10) return null;
    if (!Number.isInteger(cutoffHour) || cutoffHour < 1 || cutoffHour > 23) return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(validUntil)) return null;
    const parts = new Intl.DateTimeFormat('en-GB', {timeZone:'Europe/London',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',hourCycle:'h23'}).formatToParts(now);
    const part = name => Number(parts.find(value => value.type === name).value);
    const date = new Date(Date.UTC(part('year'), part('month') - 1, part('day'), 12));
    const holidays = new Set(excludedDates.split(/[\s,]+/).filter(Boolean));
    const isWorkingDay = day => day.getUTCDay() !== 0 && day.getUTCDay() !== 6 && !holidays.has(day.toISOString().slice(0,10));
    if (date.toISOString().slice(0,10) > validUntil) return null;
    const sameDayDispatch = isWorkingDay(date) && part('hour') < cutoffHour;
    if (!sameDayDispatch) {
      do {
        date.setUTCDate(date.getUTCDate() + 1);
        if (date.toISOString().slice(0,10) > validUntil) return null;
      } while (!isWorkingDay(date));
    }
    const dispatchDate = new Date(date);
    let days = 0;
    while (days < transitDays) {
      date.setUTCDate(date.getUTCDate() + 1);
      const iso = date.toISOString().slice(0,10);
      if (iso > validUntil) return null;
      if (isWorkingDay(date)) days++;
    }
    return {date,dispatchDate,sameDayDispatch};
  }
  class O11Basket extends HTMLElement {
    connectedCallback() {
      this.installHorizontalScrollGuard();
      if (this.bound) return;
      this.bound = true;
      this.busy = false;
      this.loadDiscountCodes();
      this.querySelector('[data-upgrade-submit]')?.removeAttribute('disabled');
      this.updateDeliveryEstimate();
      this.dateRefresh = window.setInterval(() => this.updateDeliveryEstimate(), 60000);
      this.addEventListener('click', (event) => {
        const discount = event.target.closest('[data-remove-discount]');
        if (discount) {
          event.preventDefault();
          if (!this.busy) this.updateDiscount(discount.closest('[data-discount-form]'), discount.dataset.removeDiscount);
          return;
        }
        const step = event.target.closest('[data-quantity-step]');
        const remove = event.target.closest('[data-remove]');
        if (step) {
          event.preventDefault();
          if (this.busy) return;
          const input = step.closest('.o11-quantity').querySelector('input');
          const quantity = Math.max(0, Number(input.value) + Number(step.dataset.quantityStep));
          this.change(input.dataset.key, quantity, input.id);
        } else if (remove) {
          event.preventDefault();
          if (!this.busy) this.change(remove.dataset.remove, 0);
        }
      });
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (input.matches('[data-cart-variant]')) {
          if (!this.busy && input.checkValidity()) this.changeVariant(input);
          return;
        }
        if (input.matches('[data-quantity]')) {
          if (this.busy || !input.checkValidity()) { input.value = input.dataset.quantity; return; }
          this.change(input.dataset.key, Number(input.value), input.id);
        }
        if (input.matches('.o11-upgrade-form select')) {
          const option = input.selectedOptions[0];
          const card = input.closest('[data-bundle-upgrade]');
          if (option?.value && !option.disabled) {
            card.querySelector('[data-upgrade-total]').textContent = option.dataset.total;
          }
        }
        if (input.matches('.o11-offer-form select')) {
          const price = input.selectedOptions[0]?.dataset.price;
          if (price) input.closest('.o11-basket-offer').querySelector('[data-offer-price]').textContent = price;
        }
      });
      this.addEventListener('submit', (event) => {
        const form = event.target;
        if (this.busy) { event.preventDefault(); return; }
        if (form.matches('[data-discount-form]')) {
          event.preventDefault();
          event.stopPropagation();
          if (form.reportValidity()) this.updateDiscount(form);
        } else if (form.matches('.o11-upgrade-form')) {
          event.preventDefault();
          event.stopPropagation();
          if (form.reportValidity()) this.upgrade(form);
        } else if (form.matches('.o11-offer-form')) {
          event.preventDefault();
          event.stopPropagation();
          if (form.reportValidity()) this.addOffer(form);
        } else if (form.matches('[data-cart-form]') && event.submitter?.name !== 'checkout') {
          event.preventDefault();
          if (form.reportValidity()) {
            const updates = {};
            form.querySelectorAll('[data-quantity]').forEach(input => { updates[input.dataset.key] = Number(input.value); });
            this.mutate(this.dataset.updateUrl, {updates, note:form.querySelector('[name=note]')?.value}, this.dataset.updated);
          }
        }
      });
    }
    disconnectedCallback() {
      window.clearInterval(this.dateRefresh);
      this.scrollGuard?.abort();
    }
    installHorizontalScrollGuard() {
      this.scrollGuard?.abort();
      this.scrollGuard = new AbortController();
      const signal = this.scrollGuard.signal;
      let gesture = null;
      const reset = () => { gesture = null; };
      document.addEventListener('touchstart', event => {
        reset();
        if (event.touches.length !== 1 || (window.visualViewport?.scale || 1) > 1) return;
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('input[type="range"],textarea')) return;
        const touch = event.touches[0];
        gesture = {x:touch.clientX, y:touch.clientY, axis:null, carousel:target?.closest('.o11-offers-grid')};
      }, {passive:true, signal});
      document.addEventListener('touchmove', event => {
        if (event.touches.length !== 1 || (window.visualViewport?.scale || 1) > 1) { reset(); return; }
        if (!gesture) return;
        const dx = event.touches[0].clientX - gesture.x;
        const dy = event.touches[0].clientY - gesture.y;
        if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 5) {
          gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (gesture.axis !== 'x') return;
        const carousel = gesture.carousel;
        const max = carousel ? carousel.scrollWidth - carousel.clientWidth : 0;
        const canScroll = carousel && max > 1 && ((dx < 0 && carousel.scrollLeft < max - 1) || (dx > 0 && carousel.scrollLeft > 1));
        // Keep the extras carousel usable, but stop Safari dragging the whole page sideways.
        if (!canScroll && event.cancelable) event.preventDefault();
      }, {passive:false, signal});
      document.addEventListener('touchend', reset, {passive:true, signal});
      document.addEventListener('touchcancel', reset, {passive:true, signal});
    }
    updateDeliveryEstimate() {
      const estimate = this.querySelector('[data-delivery-estimate]');
      if (!estimate) return;
      const cutoff = Number(estimate.dataset.cutoffHour);
      const result = estimatedDeliveryDate(new Date(), Number(estimate.dataset.transitDays), cutoff, estimate.dataset.excludedDates || '', estimate.dataset.validUntil || '');
      const dated = estimate.querySelector('[data-delivery-dated]');
      const fallback = estimate.querySelector('[data-delivery-fallback]');
      const label = estimate.querySelector('[data-delivery-label]');
      const dispatch = estimate.querySelector('[data-delivery-dispatch]');
      dated.hidden = !result;
      fallback.hidden = Boolean(result);
      dispatch.hidden = !result;
      label.textContent = estimate.dataset.orderToday;
      if (!result) return;
      const {date,dispatchDate,sameDayDispatch} = result;
      const formatter = new Intl.DateTimeFormat('en-GB', {timeZone:'UTC',weekday:'long',day:'numeric',month:'long'});
      const cutoffText = `${cutoff % 12 || 12}${cutoff < 12 ? 'am' : 'pm'}`;
      if (sameDayDispatch) label.textContent = estimate.dataset.orderBefore.replace('[time]',cutoffText);
      dispatch.textContent = sameDayDispatch ? estimate.dataset.dispatchToday : estimate.dataset.dispatchOn.replace('[date]',formatter.format(dispatchDate));
      const time = estimate.querySelector('[data-delivery-date]');
      time.dateTime = date.toISOString().slice(0,10);
      time.textContent = formatter.format(date);
    }
    change(key, quantity, focusId) {
      if (!Number.isInteger(quantity) || quantity < 0) return;
      this.mutate(this.dataset.changeUrl, {id:key, quantity}, this.dataset.updated, focusId);
    }
    async cartRequest(url, payload) {
      const response = await fetch(url, {
        method: payload ? 'POST' : 'GET', credentials:'same-origin', cache:'no-store',
        headers:{Accept:'application/json', ...(payload ? {'Content-Type':'application/json'} : {})},
        ...(payload ? {body:JSON.stringify(payload)} : {})
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || this.dataset.error);
      return result;
    }
    sameLine(item, reference) {
      const properties = value => JSON.stringify(Object.entries(value || {}).sort(([a],[b]) => a.localeCompare(b)));
      return item.variant_id === (reference.variant_id || reference.id)
        && properties(item.properties) === properties(reference.properties)
        && item.selling_plan_allocation?.selling_plan?.id === reference.selling_plan_allocation?.selling_plan?.id;
    }
    async changeVariant(select) {
      const variantId = Number(select.value);
      const option = select.selectedOptions[0];
      if (this.busy || !variantId || !option || option.disabled) return;
      const draftNote = this.querySelector('[name=note]')?.value;
      const status = this.querySelector('.o11-basket-status');
      status.textContent = this.dataset.working;
      status.dataset.error = 'false';
      this.setBusy(true);
      let original, added, completed = false;
      try {
        const current = await this.cartRequest(this.dataset.cartUrl + '.js');
        original = current.items.find(item => item.key === select.dataset.key);
        if (!original || String(original.product_id) !== select.dataset.product || original.selling_plan_allocation) throw new Error(this.dataset.sizeError);
        const properties = {...original.properties};
        if (properties._o11_size_pending) properties._o11_size_pending = 'false';
        if (original.variant_id === variantId) {
          if (original.properties?._o11_size_pending === 'true') {
            await this.cartRequest(this.dataset.changeUrl, {id:original.key, quantity:original.quantity, properties});
          }
        } else {
          const result = await this.cartRequest(this.dataset.addUrl, {items:[{id:variantId, quantity:original.quantity, properties}]});
          added = result.items[0];
          const updated = await this.cartRequest(this.dataset.cartUrl + '.js');
          const source = updated.items.find(item => this.sameLine(item, original));
          if (!source || source.quantity !== original.quantity) throw new Error(this.dataset.sizeError);
          await this.cartRequest(this.dataset.changeUrl, {id:source.key, quantity:0});
        }
        completed = true;
        await this.refreshCart(this.dataset.sizeUpdated, select.id, draftNote);
      } catch (error) {
        if (completed) { location.reload(); return; }
        if (added && original) {
          try {
            const current = await this.cartRequest(this.dataset.cartUrl + '.js');
            if (!current.items.some(item => this.sameLine(item, original))) {
              await this.refreshCart(this.dataset.sizeUpdated, select.id, draftNote);
              return;
            }
            const replacement = current.items.find(item => this.sameLine(item, added));
            if (replacement) await this.cartRequest(this.dataset.changeUrl, {id:replacement.key, quantity:Math.max(0, replacement.quantity - original.quantity)});
          } catch {}
        }
        try {
          await this.refreshCart(error.message || this.dataset.sizeError, select.id, draftNote, true);
        } catch { location.reload(); }
      }
    }
    async addOffer(form) {
      if (this.busy) return;
      const data = new FormData(form);
      const note = this.querySelector('[name=note]');
      const noteValue = note?.value;
      const shouldSaveNote = note && noteValue !== note.defaultValue;
      const status = this.querySelector('.o11-basket-status');
      status.textContent = this.dataset.working;
      status.dataset.error = 'false';
      this.setBusy(true);
      try {
        if (shouldSaveNote) {
          const savedNote = await fetch(this.dataset.updateUrl, {method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({note:noteValue})});
          if (!savedNote.ok) throw new Error(this.dataset.error);
        }
        const returnUrl = new URL(this.dataset.cartUrl, location.origin);
        const current = new URL(location.href);
        for (const name of ['view','preview_theme_id','pb']) {
          if (current.searchParams.has(name)) returnUrl.searchParams.set(name,current.searchParams.get(name));
        }
        data.set('return_to',returnUrl.pathname + returnUrl.search);
        // Submit through Shopify's native product form so cart-app Ajax hooks
        // do not open another drawer over this basket page.
        const submitForm = document.createElement('form');
        submitForm.method = 'POST';
        submitForm.action = form.action;
        submitForm.hidden = true;
        for (const [name,value] of data) {
          if (typeof value !== 'string') continue;
          const field = document.createElement('input');
          field.type = 'hidden'; field.name = name; field.value = value;
          submitForm.append(field);
        }
        document.body.append(submitForm);
        HTMLFormElement.prototype.submit.call(submitForm);
      } catch (error) {
        status.textContent = error.message || this.dataset.error;
        status.dataset.error = 'true';
        this.setBusy(false);
      }
    }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('button,input,select').forEach(control => {
        if (value) { control.dataset.wasDisabled = String(control.disabled); control.disabled = true; }
        else { control.disabled = control.dataset.wasDisabled === 'true'; delete control.dataset.wasDisabled; }
      });
    }
    discountCodes(cart) {
      if (Array.isArray(cart.discount_codes)) {
        return cart.discount_codes.filter(entry => entry.applicable === true).map(entry => entry.code);
      }
      const applications = [...(cart.cart_level_discount_applications || []), ...(cart.discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(entry => entry.discount_application))];
      return [...new Set(applications.filter(entry => entry?.type === 'discount_code').map(entry => entry.title))];
    }
    renderDiscountCodes(cart) {
      const form = this.querySelector('[data-discount-form]');
      const list = form?.querySelector('[data-discount-codes]');
      if (!list) return;
      list.replaceChildren();
      for (const code of this.discountCodes(cart)) {
        const item = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = code;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.removeDiscount = code;
        remove.textContent = '\u00d7';
        remove.setAttribute('aria-label', form.dataset.removeLabel.replace('[code]', code));
        item.append(label, remove);
        list.append(item);
      }
      list.hidden = !list.children.length;
    }
    async loadDiscountCodes() {
      if (!this.querySelector('[data-discount-form]')) return;
      try {
        const cart = await this.cartRequest(this.dataset.cartUrl + '.js');
        if (this.isConnected && !this.busy) this.renderDiscountCodes(cart);
      } catch {}
    }
    async updateDiscount(form, removeCode) {
      if (this.busy) return;
      const input = form.querySelector('[name=discount]');
      const code = input.value.trim();
      if (!removeCode && !code) { input.value = ''; input.reportValidity(); return; }
      const status = form.querySelector('.o11-discount-status');
      const draftNote = this.querySelector('[name=note]')?.value;
      status.textContent = this.dataset.working;
      status.dataset.error = 'false';
      input.removeAttribute('aria-invalid');
      this.setBusy(true);
      let saved = false;
      try {
        const current = await this.cartRequest(this.dataset.cartUrl + '.js');
        const existing = this.discountCodes(current);
        const requested = removeCode ? [] : code.split(',').map(value => value.trim()).filter(Boolean);
        const codes = removeCode ? existing.filter(value => value.toLowerCase() !== removeCode.toLowerCase()) : [...new Map([...existing, ...requested].map(value => [value.toLowerCase(), value])).values()];
        const payload = {discount:codes.join(',')};
        if (draftNote !== undefined) payload.note = draftNote;
        const result = await this.cartRequest(this.dataset.updateUrl, payload);
        saved = true;
        const active = this.discountCodes(result).map(value => value.toLowerCase());
        const invalid = removeCode ? active.includes(removeCode.toLowerCase()) : Array.isArray(result.discount_codes) && requested.some(value => !active.includes(value.toLowerCase()));
        const confirmed = removeCode ? !active.includes(removeCode.toLowerCase()) : requested.every(value => active.includes(value.toLowerCase()));
        const message = invalid ? (removeCode ? form.dataset.error : form.dataset.invalid) : confirmed ? (removeCode ? form.dataset.removed : form.dataset.applied) : form.dataset.pending;
        const fresh = await this.refreshCart('', undefined, draftNote);
        fresh.renderDiscountCodes(result);
        const freshForm = fresh.querySelector('[data-discount-form]');
        const freshStatus = freshForm.querySelector('.o11-discount-status');
        freshStatus.textContent = message;
        freshStatus.dataset.error = String(invalid);
        if (invalid) {
          const freshInput = freshForm.querySelector('[name=discount]');
          freshInput.value = code;
          freshInput.setAttribute('aria-invalid', 'true');
        }
        freshStatus.focus({preventScroll:true});
      } catch {
        if (saved) { location.reload(); return; }
        status.textContent = form.dataset.error;
        status.dataset.error = 'true';
        this.setBusy(false);
        status.focus({preventScroll:true});
      }
    }
    async refreshCart(message, focusId, draftNote, isError = false) {
      const renderUrl = new URL(this.dataset.cartUrl, location.origin);
      const current = new URL(location.href);
      for (const name of ['view','preview_theme_id','pb']) {
        if (current.searchParams.has(name)) renderUrl.searchParams.set(name, current.searchParams.get(name));
      }
      renderUrl.searchParams.set('section_id', this.dataset.section);
      const rendered = await fetch(renderUrl, {credentials:'same-origin',cache:'no-store'});
      if (!rendered.ok) throw new Error('Cart refresh failed');
      const html = new DOMParser().parseFromString(await rendered.text(), 'text/html');
      const fresh = html.querySelector('o11-basket');
      if (!fresh) throw new Error('Cart refresh failed');
      this.replaceWith(fresh);
      if (draftNote !== undefined && fresh.querySelector('[name=note]')) fresh.querySelector('[name=note]').value = draftNote;
      fresh.querySelector('.o11-basket-status').textContent = message;
      fresh.querySelector('.o11-basket-status').dataset.error = String(isError);
      const cartLink = document.querySelector('.o11-cart');
      if (cartLink) {
        let badge = cartLink.querySelector('span');
        if (Number(fresh.dataset.count) > 0) {
          if (!badge) { badge = document.createElement('span'); cartLink.append(badge); }
          badge.textContent = fresh.dataset.count;
        } else if (badge) badge.remove();
      }
      const focus = focusId ? document.getElementById(focusId) : fresh.querySelector('.o11-basket-status');
      if (focus) focus.focus({preventScroll:true});
      return fresh;
    }
    async upgrade(form) {
      if (this.busy || !form.reportValidity()) return;
      const select = form.querySelector('[name="bundle_variant"]');
      const option = select?.selectedOptions[0];
      if (!option?.value || option.disabled) return;
      const variantId = option.value;
      const draftNote = this.querySelector('[name=note]')?.value;
      const status = this.querySelector('.o11-basket-status');
      status.textContent = this.dataset.working;
      status.dataset.error = 'false';
      this.setBusy(true);
      try {
        const currentResponse = await fetch(this.dataset.cartUrl + '.js', {credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
        if (!currentResponse.ok) throw new Error(this.dataset.upgradeError);
        const current = await currentResponse.json();
        const mat = current.items.find(item => item.key === form.dataset.matKey);
        if (current.items.length !== 1 || !mat || String(mat.product_id) !== form.dataset.matProduct || mat.quantity !== Number(form.dataset.matQuantity) || mat.selling_plan_allocation) {
          await this.refreshCart(this.dataset.upgradeStale, undefined, draftNote, true);
          return;
        }
        const payload = {updates:{[mat.key]:mat.quantity - 1,[variantId]:1}};
        if (draftNote !== undefined) payload.note = draftNote;
        const response = await fetch(this.dataset.updateUrl, {method:'POST',credentials:'same-origin',headers:{Accept:'application/json','Content-Type':'application/json'},body:JSON.stringify(payload)});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.upgradeError);
        const bundles = result.items.filter(item => String(item.product_id) === form.dataset.bundleProduct && String(item.variant_id) === variantId);
        const remainingMats = result.items.filter(item => String(item.product_id) === form.dataset.matProduct).reduce((total,item) => total + item.quantity, 0);
        const confirmed = bundles.length === 1 && bundles[0].quantity === 1 && remainingMats === mat.quantity - 1;
        await this.refreshCart(confirmed ? this.dataset.upgradeSuccess : this.dataset.upgradeError, undefined, draftNote, !confirmed);
      } catch (error) {
        try {
          await this.refreshCart(error.message || this.dataset.upgradeError, undefined, draftNote, true);
        } catch {
          location.reload();
        }
      }
    }
    async mutate(url, payload, message, focusId) {
      if (this.busy) return;
      const draftNote = this.querySelector('[name=note]')?.value;
      const status = this.querySelector('.o11-basket-status');
      status.textContent = this.dataset.working;
      status.dataset.error = 'false';
      this.setBusy(true);
      let saved = false;
      try {
        const formData = payload instanceof FormData;
        const response = await fetch(url, {
          method:'POST', credentials:'same-origin',
          headers:formData ? {'Accept':'application/json'} : {'Accept':'application/json','Content-Type':'application/json'},
          body:formData ? payload : JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.error);
        saved = true;
        await this.refreshCart(message, focusId, draftNote);
      } catch (error) {
        if (saved) { location.reload(); return; }
        status.textContent = error.message || this.dataset.error;
        status.dataset.error = 'true';
        this.querySelectorAll('[data-quantity]').forEach(input => {input.value = input.dataset.quantity;});
        this.setBusy(false);
        status.focus({preventScroll:true});
      }
    }
  }
  customElements.define('o11-basket', O11Basket);
}


